// src/pages/products.ts —— 06 C-13 卡产品与风控：Tab0 卡段 BIN（GET / sync / PUT /api/admin/card-products）；Tab1 风控限额、Tab2 命中记录由 pages/platform.ts riskViews 提供
import { ApiError } from '../../../shared/src/api/client';
import type { AdminPage } from '../../../shared/src/api/types';
import { interpolate } from '../../../shared/src/errors';
import { fmtAmount, parseDecimalInput, toMicro } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openDrawer, openForm, reloadCurrent, sessionGuard } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_PRODUCTS, REWRITES_PRODUCTS } from '../i18n/products';
import { openCardDrawer } from './cards';
import { enabledLabel, riskViews } from './platform';
import { defineView, type ActionDef, type DrawerSpec, type FieldDef, type FormDef, type FormVals, type Need, type PageMap } from './types';
import { cell, chip, chipOf, withBadge, type CellVals, type ChipKind } from './ui';

// backend/internal/domain/card/product/read.go:89-132 列表行
export interface AdminProduct {
  id: number;
  upstream_id: number;
  upstream_group_id: number | null;
  name: string;
  name_en: string | null;
  scene: string | null;
  scene_en: string | null;
  bin: string;
  brand: string;
  area: string;
  currency: string;
  card_max_count: number;
  validity: number;
  min_amount: string;
  int_amount_only: boolean;
  supports_3ds: boolean;
  upstream_cost: unknown;
  cost_open_card: string;
  cost_close: string;
  open_fee: string;
  close_fee: string;
  tx_fee_rate: string;
  tx_fee_fixed: string;
  cross_border_rate: string;
  cross_border_fixed: string;
  declined_fee: string;
  dispute_fee: string;
  refund_fee_rate: string;
  auth_cancel_fee_rate: string;
  auth_cancel_fee_fixed: string;
  auth_cancel_fee_min: string;
  limit_amount: string;
  stock: number;
  face_color: 'black' | 'purple' | 'red' | 'green';
  enabled: boolean;
  card_open: boolean;
  card_freeze: boolean;
  card_close: boolean;
  card_custom: boolean | null;
  budget_status: string | null;
  upstream_remaining: number | null;
  margin_inverted: boolean;
  synced_at: string | null;
  updated_at: string;
}

// 抽屉「用户卡片」列表只用到的卡行子集（GET /api/admin/cards，cardq.AdminCardRow）
interface ProductCardRow {
  id: number;
  card_no: string;
  user: { uid: string; email: string | null };
  available: string;
  status: string;
}

const EDIT: Need = { module: 'products', bit: 'edit' };
const PUT_ROUTE = 'PUT /api/admin/card-products/{id}';

// 原型 L513 FACES（admin 版四色）逐字
const FACES: Readonly<Record<AdminProduct['face_color'], string>> = {
  black: 'linear-gradient(135deg,#0f1a17,#2a3833)',
  green: 'linear-gradient(135deg,#0b5c41,#39c88f)',
  purple: 'linear-gradient(135deg,#2e1f7a,#8a6cf5)',
  red: 'linear-gradient(135deg,#8a1a3b,#f07a4a)',
};
const FACE_OPTIONS = ['black', 'purple', 'red', 'green'] as const;

// 金额：D8-12 截断展示；后端值非法时原样显示（不抛错）
function money(v: string, dp: number): string {
  try {
    return fmtAmount(v, dp);
  } catch {
    return v;
  }
}

// 十进制串去尾随 0（"2.500000" → "2.5"）
function trimDec(v: string): string {
  const n = parseDecimalInput(v, 6);
  if (n === null) return v;
  return n.includes('.') ? n.replace(/\.?0+$/, '') : n;
}

function nameOf(ctx: AdminCtx, r: AdminProduct): string {
  return ctx.lang === 'zh' ? r.name : r.name_en || r.name;
}

function budgetCell(ctx: AdminCtx, r: AdminProduct): CellVals {
  const t = ctx.t;
  if (r.budget_status === null) return chip('gray', str(t, 'budgetNone'));
  const map: Readonly<Record<string, readonly [ChipKind, string]>> = { active: ['green', str(t, 'stActive')], deleted: ['red', str(t, 'stBudgetDeleted')] };
  return chipOf(map, r.budget_status, 'budget_status');
}

function capsText(ctx: AdminCtx, r: AdminProduct): string {
  const t = ctx.t;
  const mark = (b: boolean): string => (b ? '✓' : '✗');
  return `${str(t, 'capOpen')} ${mark(r.card_open)} · ${str(t, 'capFreeze')} ${mark(r.card_freeze)} · ${str(t, 'capClose')} ${mark(r.card_close)}`;
}

function yesNo(ctx: AdminCtx, b: boolean | null): string {
  return b === null ? '—' : str(ctx.t, b ? 'optYes' : 'optNo');
}

function upInfoText(r: AdminProduct): string {
  return `${r.upstream_group_id ?? '—'} · ${r.currency} · ${r.card_max_count} · ${r.validity}`;
}

function remainingText(ctx: AdminCtx, r: AdminProduct): string {
  return r.upstream_remaining === null ? str(ctx.t, 'budgetNone') : interpolate(str(ctx.t, 'upRemain'), { n: r.upstream_remaining });
}

// 06 C-13 Tab0 逐列
function productCells(ctx: AdminCtx, r: AdminProduct): readonly CellVals[] {
  const t = ctx.t;
  const bin = cell(`${r.bin} **** ****`, nameOf(ctx, r), true);
  const sellable = r.upstream_remaining === null ? 0 : Math.min(r.stock, r.upstream_remaining);
  return [
    r.margin_inverted ? withBadge(bin, 'red', str(t, 'marginInverted'), str(t, 'marginInvertedTip')) : bin,
    cell(`${r.brand} · ${r.area}`, r.currency),
    cell(`$${money(r.open_fee, 2)}`, `${str(t, 'costOpen')} $${money(r.cost_open_card, 2)}`),
    cell(`$${money(r.close_fee, 2)}`, `${str(t, 'costOpen')} $${money(r.cost_close, 2)}`),
    cell(`${trimDec(r.tx_fee_rate)}% + $${money(r.tx_fee_fixed, 2)}`),
    cell(`$${money(r.limit_amount, 0)}`),
    sellable <= 0 ? chip('red', '0') : cell(r.stock, remainingText(ctx, r)),
    budgetCell(ctx, r),
    { ...chip(r.enabled ? 'green' : 'gray', str(t, r.enabled ? 'stOn' : 'stOff')), sub: r.card_open ? '' : str(t, 'cardOpenOff') },
  ];
}

// 停用 / 启用（行内与抽屉共用）：1303 → toast err1303 + 超限代理首行（06 C-13「toast 显示同文案首行」）。
// 请求返回时会话已换（另一账号已进壳）→ 结果不再提示、不刷新；其余错误上抛由调用方（行动作 / 确认框）按同一守卫处理
async function toggleEnabled(ctx: AdminCtx, r: AdminProduct): Promise<void> {
  const t = ctx.t;
  const alive = sessionGuard(ctx);
  try {
    await ctx.gp.api.call<AdminProduct>(PUT_ROUTE, { path: { id: r.id }, body: { enabled: !r.enabled } });
  } catch (e) {
    if (!alive()) return;
    if (e instanceof ApiError && e.code === 1303) {
      // 0045：停用使用户链奖励超出开卡上限（data.field=l1Open），不再返回超限代理列表
      ctx.gp.ui.toast(str(t, 'err1303'));
      return;
    }
    throw e;
  }
  if (!alive()) return;
  ctx.gp.ui.toast(str(t, 'toastSaved'));
  await reloadCurrent(ctx);
}

const MONEY_KEYS = ['open_fee', 'cost_open_card', 'close_fee', 'tx_fee_fixed', 'cross_border_fixed', 'declined_fee', 'dispute_fee', 'auth_cancel_fee_fixed', 'auth_cancel_fee_min', 'cost_close', 'limit_amount'] as const;
const RATE_KEYS = ['tx_fee_rate', 'cross_border_rate', 'refund_fee_rate', 'auth_cancel_fee_rate'] as const;
const TEXT_KEYS = ['name', 'name_en', 'scene', 'scene_en'] as const;
const SWITCH_KEYS = ['enabled', 'int_amount_only', 'supports_3ds'] as const;
type MoneyKey = (typeof MONEY_KEYS)[number];
type RateKey = (typeof RATE_KEYS)[number];

function s(v: FormVals, k: string): string {
  const x = v[k];
  return typeof x === 'string' ? x.trim() : '';
}

function sameDecimal(a: string, b: string): boolean {
  try {
    return toMicro(a) === toMicro(b);
  } catch {
    return false;
  }
}

function decimalOk(v: string, dp: number): boolean {
  return parseDecimalInput(v, dp) !== null;
}

// 06「前端只提交改动字段」：金额 / 费率按微单位比较，stock 转 number，开关按布尔比较
export function productDiff(r: AdminProduct, v: FormVals): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const k of TEXT_KEYS) if (s(v, k) !== (r[k] ?? '')) out[k] = s(v, k);
  for (const k of [...MONEY_KEYS, ...RATE_KEYS]) {
    const nv = parseDecimalInput(s(v, k), 6) ?? s(v, k);
    if (!sameDecimal(nv, r[k])) out[k] = nv;
  }
  if (Number(s(v, 'stock')) !== r.stock) out['stock'] = Number(s(v, 'stock'));
  if (s(v, 'face_color') !== r.face_color) out['face_color'] = s(v, 'face_color');
  for (const k of SWITCH_KEYS) if ((v[k] === true) !== r[k]) out[k] = v[k] === true;
  return out;
}

export function productForm(ctx: AdminCtx, r: AdminProduct): FormDef {
  const t = ctx.t;
  const ro = (k: string, label: string, wide = false): FieldDef => ({ k, label, type: 'readonly', wide });
  const num = (k: MoneyKey | RateKey | 'stock', label: string): FieldDef => ({ k, label, type: 'number' });
  const sw = (k: (typeof SWITCH_KEYS)[number], label: string): FieldDef => ({ k, label, type: 'switch' });
  const tip = `${str(t, 'productFormTip')} ${str(t, 'cLimit')}：${str(t, 'limitTip')}；${str(t, 'cStock')}：${interpolate(str(t, 'stockTip'), { n: r.upstream_remaining ?? '—' })}`;
  return {
    title: `${str(t, 'editProduct')} · ${r.bin}`,
    tip,
    // 06 C-13 表单表逐行：上游只读项在前，本地可编辑项在后
    fields: [
      ro('bin', str(t, 'cBin')),
      ro('brand', str(t, 'cBrand')),
      ro('area', 'Region'),
      { k: 'name', label: str(t, 'cProdName'), type: 'text' },
      { k: 'name_en', label: str(t, 'cProdNameEn'), type: 'text', optional: true },
      { k: 'scene', label: str(t, 'cScene'), type: 'text', wide: true, optional: true, placeholder: str(t, 'sceneTip') },
      { k: 'scene_en', label: str(t, 'cSceneEn'), type: 'text', wide: true, optional: true },
      num('open_fee', `${str(t, 'cOpenFee')} (USD)`),
      num('cost_open_card', `${str(t, 'cCostOpen')} (USD)`),
      num('close_fee', `${str(t, 'cCloseFee')} (USD)`),
      num('tx_fee_rate', str(t, 'cfgTxFeeRate')),
      num('tx_fee_fixed', str(t, 'cfgTxFeeFixed')),
      num('cross_border_rate', str(t, 'cfgCrossRate')),
      num('cross_border_fixed', str(t, 'cfgCrossFixed')),
      num('declined_fee', str(t, 'cfgDeclinedFee')),
      num('dispute_fee', str(t, 'cfgDisputeFee')),
      num('refund_fee_rate', `${str(t, 'cRefundFeeRate')} (%)`),
      num('auth_cancel_fee_rate', `${str(t, 'cAuthCancelFeeRate')} (%)`),
      num('auth_cancel_fee_fixed', `${str(t, 'cAuthCancelFeeFixed')} (USDT)`),
      num('auth_cancel_fee_min', `${str(t, 'cAuthCancelFeeMin')} (USD)`),
      num('cost_close', `${str(t, 'cCostClose')} (USD)`),
      num('limit_amount', `${str(t, 'cLimit')} (USD)`),
      num('stock', str(t, 'cStock')),
      { k: 'face_color', label: 'Card face', type: 'select', options: FACE_OPTIONS },
      sw('enabled', str(t, 'cEnabled')),
      sw('int_amount_only', str(t, 'cIntOnly')),
      sw('supports_3ds', str(t, 'c3ds')),
      ro('caps', str(t, 'capsLabel'), true),
      ro('upInfo', str(t, 'upInfo'), true),
      ro('upMisc', `${str(t, 'cMinAmount')} / ${str(t, 'cCustomHolder')} / ${str(t, 'cValidity')}`, true),
      ro('upCostRaw', str(t, 'upCost'), true),
    ],
    init: {
      bin: r.bin,
      brand: r.brand,
      area: r.area,
      name: r.name,
      name_en: r.name_en ?? '',
      scene: r.scene ?? '',
      scene_en: r.scene_en ?? '',
      // 十进制串去尾随 0 展示（"2.500000" → "2.5"），提交前按微单位与原值比较
      ...Object.fromEntries([...MONEY_KEYS, ...RATE_KEYS].map((k) => [k, trimDec(r[k])] as const)),
      stock: String(r.stock),
      face_color: r.face_color,
      enabled: r.enabled,
      int_amount_only: r.int_amount_only,
      supports_3ds: r.supports_3ds,
      caps: capsText(ctx, r),
      upInfo: upInfoText(r),
      upMisc: `$${money(r.min_amount, 2)} / ${yesNo(ctx, r.card_custom)} / ${r.validity}`,
      upCostRaw: JSON.stringify(r.upstream_cost ?? null),
    },
    validate: (v, d) => {
      const name = s(v, 'name');
      if (name === '' || [...name].length > 64 || [...s(v, 'name_en')].length > 64) return str(d, 'errNameLen');
      if (MONEY_KEYS.some((k) => !decimalOk(s(v, k), 6))) return str(d, 'errAmountFmt');
      if (RATE_KEYS.some((k) => !decimalOk(trimDec(s(v, k)), 4) || toMicro(s(v, k)) > toMicro('100'))) return str(d, 'errFeeRate');
      if (!/^\d+$/.test(s(v, 'stock'))) return str(d, 'errStockInt');
      // 06：本次提交含 limit_amount 时须 ≥ min_amount（后端同校验 4220）
      const limit = s(v, 'limit_amount');
      if (!sameDecimal(limit, r.limit_amount) && decimalOk(r.min_amount, 6) && toMicro(limit) < toMicro(r.min_amount)) return interpolate(str(d, 'errLimitMin'), { min: `$${money(r.min_amount, 2)}` });
      return '';
    },
    submit: async (c, v) => {
      const body = productDiff(r, v);
      if (Object.keys(body).length === 0) {
        c.gp.ui.toast(str(c.t, 'toastSaved')); // 无改动：不发请求，直接关闭
        return;
      }
      // 1303（用户链奖励超出新开卡上限，06 C-13 上限漂移校验）由 submitForm 经 ui.err 进表单红条
      const alive = sessionGuard(c);
      await c.gp.api.call<AdminProduct>(PUT_ROUTE, { path: { id: r.id }, body });
      if (!alive()) return; // 会话已换：不提示、不刷新（表单收尾由 submitForm 按同一守卫跳过）
      c.gp.ui.toast(str(c.t, 'toastSaved'));
      void reloadCurrent(c);
    },
  };
}

// 0040 授权撤销费：「[比例% + ]固定 USDT（≥ $门槛）」；门槛 0 显示「每笔」，比例为 0 时省略比例段
export function authCancelText(ctx: AdminCtx, r: Pick<AdminProduct, 'auth_cancel_fee_rate' | 'auth_cancel_fee_fixed' | 'auth_cancel_fee_min'>): string {
  const t = ctx.t;
  const rate = sameDecimal(r.auth_cancel_fee_rate, '0') ? '' : `${trimDec(r.auth_cancel_fee_rate)}% + `;
  const when = sameDecimal(r.auth_cancel_fee_min, '0') ? str(t, 'authCancelEvery') : `≥ $${money(r.auth_cancel_fee_min, 2)}`;
  return `${rate}${money(r.auth_cancel_fee_fixed, 2)} USDT（${when}）`;
}

function productDrawer(ctx: AdminCtx, r: AdminProduct, cards: readonly ProductCardRow[]): DrawerSpec {
  const t = ctx.t;
  const tz = ctx.host.state.sh.timezone || undefined;
  return {
    kind: str(t, 'products'),
    title: `${r.bin} · ${r.brand}`,
    subtitle: nameOf(ctx, r),
    card: { style: `background:${FACES[r.face_color] ?? FACES.black};color:#fff;border-radius:16px;padding:18px;display:flex;flex-direction:column;gap:22px`, balance: '—', brand: r.brand === 'MASTERCARD' ? 'MC' : r.brand, no: `${r.bin} **** ****` },
    // 06 C-13 抽屉字段格顺序
    fields: [
      { k: str(t, 'cProdName'), v: nameOf(ctx, r) },
      { k: `${str(t, 'cOpenFee')} · ${str(t, 'cCostOpen')}`, v: `$${money(r.open_fee, 2)} / $${money(r.cost_open_card, 2)}` },
      { k: `${str(t, 'cCloseFee')} · ${str(t, 'cCostClose')}`, v: `$${money(r.close_fee, 2)} / $${money(r.cost_close, 2)}` },
      { k: str(t, 'cTxFee'), v: `${trimDec(r.tx_fee_rate)}% + $${money(r.tx_fee_fixed, 2)}` },
      { k: str(t, 'cCrossFee'), v: `${trimDec(r.cross_border_rate)}% + $${money(r.cross_border_fixed, 2)}` },
      { k: str(t, 'cDeclineDispute'), v: `$${money(r.declined_fee, 2)} / $${money(r.dispute_fee, 2)}` },
      { k: str(t, 'cAuthCancelFee'), v: authCancelText(ctx, r) },
      { k: str(t, 'cLimit'), v: `$${money(r.limit_amount, 0)}` },
      { k: '3DS', v: yesNo(ctx, r.supports_3ds) },
      { k: `${str(t, 'cStock')} · ${str(t, 'upRemain').replace(/\s*\{n\}/, '')}`, v: `${r.stock} / ${r.upstream_remaining ?? str(t, 'budgetNone')}` },
      { k: str(t, 'cBudget'), v: budgetCell(ctx, r).v },
      { k: str(t, 'cMinAmount'), v: `$${money(r.min_amount, 2)}` },
      { k: str(t, 'capsLabel'), v: capsText(ctx, r), wide: true },
      { k: str(t, 'upInfo'), v: upInfoText(r), wide: true },
      { k: str(t, 'cScene'), v: (ctx.lang === 'zh' ? r.scene : r.scene_en || r.scene) || '—', wide: true },
      { k: 'upstream product_id', v: String(r.upstream_id), wide: true },
      { k: str(t, 'cSyncedAt'), v: fmtDateTime(r.synced_at, { tz }) },
    ],
    json: JSON.stringify(r.upstream_cost ?? null, null, 2),
    jsonTitle: str(t, 'upCost'),
    list: {
      title: str(t, 'cards'),
      rows: cards.map((c) => ({ left: c.card_no, sub: c.user.email ?? c.user.uid, right: `$${money(c.available, 2)}`, rightSub: c.status, go: (x: AdminCtx) => openCardDrawer(x, c.id) })),
    },
    actions: [
      {
        label: r.enabled ? 'stOff' : 'stOn',
        kind: '',
        need: EDIT,
        run: async (c) => {
          closeDrawer(c);
          await toggleEnabled(c, r);
        },
      },
      { label: 'aEdit', kind: 'primary', need: EDIT, run: (c) => openForm(c, productForm(c, r)) },
    ],
  };
}

// 行点击：先开抽屉，再取该产品名下前 10 张卡回填列表（会话已换、或当前抽屉已不是本次打开的那一个则丢弃）
function openProduct(ctx: AdminCtx, r: AdminProduct): void {
  const spec = productDrawer(ctx, r, []);
  openDrawer(ctx, spec);
  const alive = sessionGuard(ctx);
  void ctx.gp.api
    .call<AdminPage<ProductCardRow>>('GET /api/admin/cards', { query: { product_id: r.id, page: 1, size: 10 } })
    .then((p) => {
      if (!alive() || ctx.host.state.tb.drawer !== spec) return;
      const rows = Array.isArray(p.list) ? p.list.slice(0, 10) : [];
      openDrawer(ctx, productDrawer(ctx, r, rows));
    })
    .catch((e: unknown) => {
      if (alive()) console.warn('[gp:admin] 产品名下卡片读取失败', r.id, e);
    });
}

const binView = defineView<AdminProduct>({
  key: 'products',
  source: { route: 'GET /api/admin/card-products', tabParam: 'none' },
  echo: '/admin/card-products',
  noDate: true,
  noKeyword: true,
  cols: [{ label: 'cBin' }, { label: 'cBrand' }, { label: 'cOpenFee' }, { label: 'cCloseFee' }, { label: 'cTxFee' }, { label: 'cLimit' }, { label: 'cStock' }, { label: 'cBudget' }, { label: 'cEnabled', facet: 'enabled', facetLabel: enabledLabel }],
  cells: productCells,
  open: openProduct,
  rowActions: (): readonly ActionDef<AdminProduct>[] => [
    { label: 'aEdit', need: EDIT, run: (c, r) => openForm(c, productForm(c, r)) },
    // 停用向为 danger（原型 L786）；文案取原型 stOff / stOn
    { label: 'stOff', need: EDIT, danger: true, show: (r) => r.enabled, run: (c, r) => toggleEnabled(c, r) },
    { label: 'stOn', need: EDIT, show: (r) => !r.enabled, run: (c, r) => toggleEnabled(c, r) },
  ],
  note: (t) => str(t, 'productsNote'),
  primary: {
    label: 'syncProducts',
    icon: '↻ ',
    need: EDIT,
    run: async (c) => {
      const alive = sessionGuard(c);
      const r = await c.gp.api.call<{ total: number; created: number; updated: number; disabled: number }>('POST /api/admin/card-products/sync');
      if (!alive()) return; // 会话已换：同步结果不提示、不刷新
      c.gp.ui.toast(interpolate(str(c.t, 'toastSyncProducts'), { created: r.created, updated: r.updated, disabled: r.disabled }));
      await reloadCurrent(c);
    },
  },
  csvName: 'products',
});

export const PAGES_PRODUCTS: PageMap = {
  products: {
    page: 'products',
    tabLabels: (t) => [...strArr(t, 'tabProducts'), str(t, 'tabHits')], // 原型两页签 + 新增「命中记录」（M-03）
    views: [binView, ...riskViews()], // 风控两页签（06 C-13 Tab1 / Tab2）由 pages/platform.ts 提供
    i18n: I18N_PRODUCTS, // TD-849：由 table 域 mergePagePacks 并入字典
    rewrites: REWRITES_PRODUCTS,
  },
};
